import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Users, CreditCard, FileText, Play } from 'lucide-react';
import api, { formatCurrency } from '../services/api';

export default function PayrollModule() {
  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [runs, setRuns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRunForm, setShowRunForm] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [form, setForm] = useState({
    name: '', designation: '', department: '', pan: '',
    bank_account: '', ifsc: '', uan: '', date_of_joining: '',
    basic_salary: '', hra: '', da: '', conveyance: '', special_allowance: ''
  });
  const [runMonth, setRunMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadEmployees(); loadRuns(); }, []);
  const loadEmployees = () => api.get('/payroll/employees').then(r => setEmployees(r.data || [])).catch(() => {});
  const loadRuns = () => api.get('/payroll/runs').then(r => setRuns(r.data.runs || [])).catch(() => {});
  const u = (f, v) => setForm(s => ({ ...s, [f]: v }));

  const basic = parseFloat(form.basic_salary) || 0;
  const hra = parseFloat(form.hra) || 0;
  const da = parseFloat(form.da) || 0;
  const conv = parseFloat(form.conveyance) || 0;
  const special = parseFloat(form.special_allowance) || 0;
  const gross = basic + hra + da + conv + special;
  const pf = Math.round(Math.min(basic, 15000) * 0.12 * 100) / 100;
  const esi = gross <= 21000 ? Math.round(gross * 0.0075 * 100) / 100 : 0;
  const pt = gross > 15000 ? 200 : 0;
  const net = Math.round((gross - pf - esi - pt) * 100) / 100;

  const handleCreateEmployee = async () => {
    if (!form.name || !form.basic_salary) { toast.error('Fill name and basic salary'); return; }
    setLoading(true);
    try {
      await api.post('/payroll/employees', form);
      toast.success('Employee added'); setShowForm(false);
      setForm({ name: '', designation: '', department: '', pan: '', bank_account: '', ifsc: '', uan: '', date_of_joining: '', basic_salary: '', hra: '', da: '', conveyance: '', special_allowance: '' });
      loadEmployees();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleRunPayroll = async () => {
    if (!runMonth) { toast.error('Select month'); return; }
    setLoading(true);
    try {
      const res = await api.post('/payroll/runs', { month: runMonth, working_days: 30 });
      toast.success(`Payroll processed for ${runMonth}`); setShowRunForm(false); loadRuns();
      setSelectedRun(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-5" data-testid="payroll-module">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-bold text-2xl text-slate-900">Payroll Management</h1><p className="text-sm text-slate-500">Employee salary, PF, ESI, and payslip generation</p></div>
        <div className="flex gap-2">
          {tab === 'employees' && <button onClick={() => setShowForm(true)} className="btn-accent flex items-center gap-2" data-testid="add-employee-btn"><Plus className="w-4 h-4" /> Add Employee</button>}
          {tab === 'runs' && <button onClick={() => setShowRunForm(true)} className="btn-accent flex items-center gap-2" data-testid="run-payroll-btn"><Play className="w-4 h-4" /> Run Payroll</button>}
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[['employees', 'Employees'], ['runs', 'Payroll Runs']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500'}`}>{l}</button>
        ))}
      </div>

      {showForm && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">Add Payroll Employee</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="form-label">Name</label><input type="text" value={form.name} onChange={e => u('name', e.target.value)} className="form-input" data-testid="emp-name" /></div>
            <div><label className="form-label">Designation</label><input type="text" value={form.designation} onChange={e => u('designation', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Department</label><select value={form.department} onChange={e => u('department', e.target.value)} className="form-input"><option value="">Select</option><option>Operations</option><option>Accounts</option><option>Admin</option><option>Logistics</option><option>Fleet</option></select></div>
            <div><label className="form-label">Date of Joining</label><input type="date" value={form.date_of_joining} onChange={e => u('date_of_joining', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">PAN</label><input type="text" value={form.pan} onChange={e => u('pan', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">Bank A/C</label><input type="text" value={form.bank_account} onChange={e => u('bank_account', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">IFSC</label><input type="text" value={form.ifsc} onChange={e => u('ifsc', e.target.value)} className="form-input" /></div>
            <div><label className="form-label">UAN</label><input type="text" value={form.uan} onChange={e => u('uan', e.target.value)} className="form-input" /></div>
          </div>
          <h4 className="font-semibold text-sm text-slate-700">Salary Structure</h4>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div><label className="form-label">Basic</label><input type="number" value={form.basic_salary} onChange={e => u('basic_salary', e.target.value)} className="form-input font-mono text-right" data-testid="emp-basic" /></div>
            <div><label className="form-label">HRA</label><input type="number" value={form.hra} onChange={e => u('hra', e.target.value)} className="form-input font-mono text-right" /></div>
            <div><label className="form-label">DA</label><input type="number" value={form.da} onChange={e => u('da', e.target.value)} className="form-input font-mono text-right" /></div>
            <div><label className="form-label">Conveyance</label><input type="number" value={form.conveyance} onChange={e => u('conveyance', e.target.value)} className="form-input font-mono text-right" /></div>
            <div><label className="form-label">Special Allow.</label><input type="number" value={form.special_allowance} onChange={e => u('special_allowance', e.target.value)} className="form-input font-mono text-right" /></div>
          </div>
          <div className="grid grid-cols-4 gap-3 bg-slate-50 rounded p-3">
            <div><p className="text-xs text-slate-500">Gross</p><p className="font-mono font-bold">Rs. {formatCurrency(gross)}</p></div>
            <div><p className="text-xs text-slate-500">PF (12%)</p><p className="font-mono text-red-600">Rs. {formatCurrency(pf)}</p></div>
            <div><p className="text-xs text-slate-500">ESI + PT</p><p className="font-mono text-red-600">Rs. {formatCurrency(esi + pt)}</p></div>
            <div><p className="text-xs text-slate-500">Net Salary</p><p className="font-mono font-bold text-green-600">Rs. {formatCurrency(net)}</p></div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreateEmployee} disabled={loading} className="btn-accent" data-testid="save-employee-btn">{loading ? 'Saving...' : 'Add Employee'}</button>
            <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {showRunForm && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <h3 className="font-heading font-semibold text-slate-800">Run Payroll</h3>
          <div className="flex items-end gap-3">
            <div><label className="form-label">Month</label><input type="month" value={runMonth} onChange={e => setRunMonth(e.target.value)} className="form-input" data-testid="payroll-month" /></div>
            <button onClick={handleRunPayroll} disabled={loading} className="btn-accent">{loading ? 'Processing...' : 'Process Payroll'}</button>
            <button onClick={() => setShowRunForm(false)} className="btn-secondary">Cancel</button>
          </div>
          <p className="text-sm text-slate-500">This will process salary for {employees.length} active employees.</p>
        </div>
      )}

      {tab === 'employees' && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Employee</th><th className="p-3 text-left">Designation</th><th className="p-3 text-left">Department</th><th className="p-3 text-right">Gross</th><th className="p-3 text-right">Deductions</th><th className="p-3 text-right">Net Salary</th></tr></thead>
            <tbody>{employees.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No payroll employees. Add employees to get started.</td></tr> :
              employees.map(e => (
                <tr key={e.emp_id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-3"><p className="font-medium">{e.name}</p><p className="text-xs text-slate-400">PAN: {e.pan || 'N/A'}</p></td>
                  <td className="p-3">{e.designation}</td>
                  <td className="p-3"><span className="bg-slate-100 px-2 py-0.5 rounded text-xs">{e.department || '-'}</span></td>
                  <td className="p-3 text-right font-mono">{formatCurrency(e.gross_salary)}</td>
                  <td className="p-3 text-right font-mono text-red-600">{formatCurrency(e.total_deductions)}</td>
                  <td className="p-3 text-right font-mono font-medium text-green-600">{formatCurrency(e.net_salary)}</td>
                </tr>
              ))}</tbody>
          </table>
        </div>
      )}

      {tab === 'runs' && (
        <div className="space-y-4">
          {selectedRun && (
            <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-800">Payroll: {selectedRun.month}</h3>
                <button onClick={() => setSelectedRun(null)} className="text-xs text-slate-500 hover:underline">Close</button>
              </div>
              <div className="grid grid-cols-5 gap-3">
                <div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-500">Employees</p><p className="font-mono font-bold">{selectedRun.employee_count}</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-500">Gross</p><p className="font-mono font-bold">Rs. {formatCurrency(selectedRun.total_gross)}</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-500">PF</p><p className="font-mono text-red-600">Rs. {formatCurrency(selectedRun.total_pf)}</p></div>
                <div className="bg-slate-50 rounded p-3"><p className="text-xs text-slate-500">ESI</p><p className="font-mono text-red-600">Rs. {formatCurrency(selectedRun.total_esi)}</p></div>
                <div className="bg-green-50 rounded p-3"><p className="text-xs text-slate-500">Net</p><p className="font-mono font-bold text-green-600">Rs. {formatCurrency(selectedRun.total_net)}</p></div>
              </div>
              <table className="w-full text-xs">
                <thead><tr className="bg-slate-50"><th className="p-2 text-left">Employee</th><th className="p-2 text-right">Basic</th><th className="p-2 text-right">Gross</th><th className="p-2 text-right">PF</th><th className="p-2 text-right">Net</th></tr></thead>
                <tbody>{selectedRun.slips?.map(s => (
                  <tr key={s.slip_id} className="border-t border-slate-100"><td className="p-2 font-medium">{s.emp_name}</td><td className="p-2 text-right font-mono">{formatCurrency(s.basic_salary)}</td><td className="p-2 text-right font-mono">{formatCurrency(s.gross_salary)}</td><td className="p-2 text-right font-mono text-red-600">{formatCurrency(s.pf_employee)}</td><td className="p-2 text-right font-mono font-medium text-green-600">{formatCurrency(s.net_salary)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">Month</th><th className="p-3 text-right">Employees</th><th className="p-3 text-right">Total Gross</th><th className="p-3 text-right">Total Net</th><th className="p-3 text-left">Processed By</th><th className="p-3"></th></tr></thead>
              <tbody>{runs.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-slate-400">No payroll runs yet</td></tr> :
                runs.map(r => (
                  <tr key={r.run_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => api.get(`/payroll/runs/${r.run_id}`).then(res => setSelectedRun(res.data)).catch(() => {})}>
                    <td className="p-3 font-mono font-medium">{r.month}</td>
                    <td className="p-3 text-right">{r.employee_count}</td>
                    <td className="p-3 text-right font-mono">{formatCurrency(r.total_gross)}</td>
                    <td className="p-3 text-right font-mono font-medium text-green-600">{formatCurrency(r.total_net)}</td>
                    <td className="p-3 text-sm">{r.processed_by_name}</td>
                    <td className="p-3 text-xs text-blue-600">View</td>
                  </tr>
                ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
